/**
 * Declaration Query Hooks
 * TanStack Query hooks for POA and Declaration operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { declarationService } from '../../services/api/declarationService';

/**
 * Query hook for fetching declaration by application ID
 */
export function useDeclarationQuery(applicationId) {
  return useQuery({
    queryKey: ['declaration', applicationId],
    queryFn: () => declarationService.getByApplication(applicationId),
    enabled: !!applicationId,
    // Retry on 401 (auth timing), but not on 404 (no declaration yet is valid)
    retry: (failureCount, error) => {
      if (error?.response?.status === 401 && failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000),
  });
}

/**
 * Mutation hook for saving declaration
 */
export function useSaveDeclarationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }) => declarationService.save(applicationId, data),
    onSuccess: (_, { applicationId }) => {
      // Invalidate declaration query to refetch
      queryClient.invalidateQueries({ queryKey: ['declaration', applicationId] });
      // Also invalidate applications to update declarationId link
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });
}
